
import React from 'react';
import Header from '../Header';
import Footer from '../Footer';
import { Helmet } from 'react-helmet';
import { updateMetaTags } from '@/utils/ottoSeoUtils';

interface PageLayoutProps {
  children: React.ReactNode;
  title: string;
  description: string;
  ogImage?: string;
  noindex?: boolean;
  canonicalPath?: string;
  schemaData?: any[];
  className?: string;
  customCta?: string;
  navStyle?: "light" | "default";
}

const PageLayout: React.FC<PageLayoutProps> = ({ 
  children, 
  title, 
  description, 
  ogImage = "https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/astrem_og_Websites%20Content%20Marketing%20Solutions.jpg", 
  noindex = false, 
  canonicalPath,
  schemaData = [],
  className = '',
  customCta,
  navStyle
}) => {
  const fullTitle = title.includes('Astrem') ? title : `${title} | Astrem`;
  const canonicalUrl = canonicalPath ? `https://astrem.co${canonicalPath}` : undefined;

  // Update metadata in OTTO utils for search engine crawlers
  React.useEffect(() => {
    updateMetaTags({
      title: fullTitle,
      description,
      imageUrl: ogImage,
      canonicalUrl
    });
  }, [fullTitle, description, ogImage, canonicalUrl]);

  return (
    <div className={`min-h-screen flex flex-col ${className}`}>
      <Helmet>
        <title>{fullTitle}</title>
        <meta name="description" content={description} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImage} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
        
        {/* Canonical URL */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        
        {/* Index control */}
        {noindex && <meta name="robots" content="noindex" />}
        
        {/* Schema.org JSON-LD */}
        {schemaData.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify(schemaData)}
          </script>
        )}
        
        {/* Custom CTA for Search Results */}
        {customCta && (
          <meta name="description-cta" content={customCta} />
        )}
      </Helmet>
      
      <Header navStyle={navStyle} />
      
      {children}
      
      <Footer />
    </div>
  );
};

export default PageLayout;
